import { useMap } from '@vis.gl/react-google-maps'
import React, { useCallback, useEffect, useMemo } from 'react'

import { MARKER_CLUSTER_MAX_ZOOM } from '../constants'
import { useSupercluster } from '../hooks/useSupercluster'
import { getFirstLocationAtMarker } from '../utils/locationFocus'
import MapClusterMarker from './MapClusterMarker'
import MapLocationMarker from './MapLocationMarker'

const MapClusteredLocationMarkers = ({
  locations,
  store,
  toggleDrawer,
  theme,
}) => {
  const map = useMap()

  const geojson = useMemo(
    () => ({
      type: 'FeatureCollection',
      features: locations.map(location => ({
        type: 'Feature',
        properties: location,
        geometry: {
          type: 'Point',
          coordinates: [location.lngNumber, location.latNumber],
        },
      })),
    }),
    [locations]
  )

  const superclusterOptions = useMemo(
    () => ({
      radius: 40,
      maxZoom: MARKER_CLUSTER_MAX_ZOOM,
    }),
    []
  )

  const { clusters, getLeaves } = useSupercluster(geojson, superclusterOptions)

  useEffect(() => {
    if (map && locations.length > 0) {
      const bounds = new google.maps.LatLngBounds()
      locations.forEach(location => {
        bounds.extend({ lat: location.latNumber, lng: location.lngNumber })
      })

      map.fitBounds(bounds, { padding: 50 })
    }
  }, [map, locations])

  const handleClusterClick = useCallback(
    (marker, clusterId) => {
      const leaves = getLeaves(clusterId)
      const bounds = new google.maps.LatLngBounds()
      leaves.forEach(leaf => {
        bounds.extend({
          lat: leaf.properties.latNumber,
          lng: leaf.properties.lngNumber,
        })
      })
      map.fitBounds(bounds, { padding: 50 })
    },
    [getLeaves, map]
  )

  const handleMarkerClick = useCallback(
    async location => {
      const focusLocation = getFirstLocationAtMarker(location, locations)
      await focusLocation.loadProviders()
      store.locationStore.setFocusLocation(focusLocation)
      toggleDrawer(true)
    },
    [locations, store, toggleDrawer]
  )

  return (
    <>
      {clusters.map(feature => {
        const [lng, lat] = feature.geometry.coordinates
        const clusterProperties = feature.properties

        if (clusterProperties.cluster) {
          return (
            <MapClusterMarker
              key={`cluster-${clusterProperties.cluster_id}`}
              clusterId={clusterProperties.cluster_id}
              position={{ lat, lng }}
              size={clusterProperties.point_count}
              sizeAsText={String(clusterProperties.point_count_abbreviated)}
              onClick={handleClusterClick}
              theme={theme}
            />
          )
        }

        return (
          <MapLocationMarker
            key={`marker-${feature.properties.id}`}
            location={feature.properties}
            onClick={handleMarkerClick}
            theme={theme}
            store={store}
          />
        )
      })}
    </>
  )
}

export default MapClusteredLocationMarkers
